import { useEffect, useState } from "react";
import type { HttpClient } from "@/shared/api/http";
import {
  deleteEntityAttachment,
  listEntityAttachments,
  openEntityAttachmentContent,
  uploadEntityAttachment,
} from "@/modules/docs/api/attachmentsApi";
import { useAuth } from "@/shared/auth/AuthContext";

export function getUserAvatarKey(userId: string) {
  return `galaxy_avatar_${userId}`;
}

export function setUserAvatar(userId: string, avatarUrl: string | null) {
  const key = getUserAvatarKey(userId);
  try {
    if (avatarUrl) {
      localStorage.setItem(key, avatarUrl);
    } else {
      localStorage.removeItem(key);
    }
  } catch (err) {
    console.error("Failed to update avatar in localStorage", err);
  }
  window.dispatchEvent(
    new CustomEvent("galaxy_avatar_changed", {
      detail: { userId, avatarUrl },
    }),
  );
}

/**
 * Uploads avatar image to server via Doc Attachment API (/api/v1/doc/attachments).
 * Automatically cleans up old avatar attachments, syncs with server, and caches locally.
 */
export async function uploadUserAvatar(
  http: HttpClient,
  userId: string,
  dataUrlOrFile: string | File | null,
): Promise<string | null> {
  // 1. Delete existing avatar attachments on server
  try {
    const existing = await listEntityAttachments(http, "User", userId);
    for (const a of existing) {
      await deleteEntityAttachment(http, a.id).catch(() => {});
    }
  } catch (err) {
    console.warn("Could not clean up existing avatar attachments", err);
  }

  // 2. If null, user removed avatar
  if (!dataUrlOrFile) {
    setUserAvatar(userId, null);
    return null;
  }

  // 3. Prepare File object and DataURL
  let file: File;
  let dataUrl: string;

  if (typeof dataUrlOrFile === "string") {
    dataUrl = dataUrlOrFile;
    const res = await fetch(dataUrlOrFile);
    const blob = await res.blob();
    const ext = blob.type.includes("webp")
      ? "webp"
      : blob.type.includes("jpeg") || blob.type.includes("jpg")
        ? "jpg"
        : "png";
    file = new File([blob], `avatar_${userId.slice(0, 8)}.${ext}`, {
      type: blob.type || "image/png",
    });
  } else {
    file = dataUrlOrFile;
    dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  // 4. Upload to server via Attachment API
  await uploadEntityAttachment(http, "User", userId, file);

  // 5. Update local cache and dispatch event for immediate reactivity
  setUserAvatar(userId, dataUrl);

  return dataUrl;
}

/**
 * Hook đồng bộ avatar của user theo userId từ backend Attachment API (/api/v1/doc/attachments).
 * Có local cache để hiển thị tức thì không bị giật/nhấp nháy, tự động revalidate từ server.
 */
export function useUserAvatar(userId?: string | null) {
  const { http } = useAuth();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(() => {
    if (!userId) return null;
    try {
      return localStorage.getItem(getUserAvatarKey(userId));
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!userId) {
      setAvatarUrl(null);
      return;
    }

    let isMounted = true;

    // 1. Read local cache first
    try {
      const cached = localStorage.getItem(getUserAvatarKey(userId));
      if (cached) setAvatarUrl(cached);
    } catch {
      // ignore
    }

    // 2. Fetch server attachment if http client is available
    if (http) {
      listEntityAttachments(http, "User", userId)
        .then(async (rows) => {
          if (!isMounted) return;
          if (rows && rows.length > 0) {
            const newest = rows[0];
            if (!newest) return;
            if (newest.readUri?.trim()) {
              setAvatarUrl(newest.readUri.trim());
              setUserAvatar(userId, newest.readUri.trim());
            } else {
              try {
                const blob = await openEntityAttachmentContent(http, newest.id);
                if (!isMounted) return;
                const reader = new FileReader();
                reader.onloadend = () => {
                  if (!isMounted) return;
                  const dataUri = reader.result as string;
                  setAvatarUrl(dataUri);
                  setUserAvatar(userId, dataUri);
                };
                reader.readAsDataURL(blob);
              } catch (err) {
                console.warn("Failed to load avatar content", err);
              }
            }
          }
        })
        .catch((err) => {
          console.warn("Failed to fetch user avatar from server", err);
        });
    }

    const handleAvatarChange = (e: Event) => {
      const custom = e as CustomEvent<{ userId: string; avatarUrl: string | null }>;
      if (!custom.detail || custom.detail.userId === userId) {
        try {
          setAvatarUrl(localStorage.getItem(getUserAvatarKey(userId)));
        } catch {
          setAvatarUrl(null);
        }
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === getUserAvatarKey(userId)) {
        setAvatarUrl(e.newValue);
      }
    };

    window.addEventListener("galaxy_avatar_changed", handleAvatarChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      isMounted = false;
      window.removeEventListener("galaxy_avatar_changed", handleAvatarChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [userId, http]);

  return avatarUrl;
}
